<template>
    <div class="autocomplete-wrapper" @focusout="onFocusOut" @keydown.esc="onEsc">
        <b-input-group class="autocomplete-input-group" :class="{'is-focused': isListVisible}">
            <b-input-group-prepend v-if="loading" is-text>
                <b-spinner small></b-spinner>
            </b-input-group-prepend>

            <b-form-input
                :id="inputId"
                ref="input"
                v-model="searchQuery"
                v-bind="$attrs"
                :placeholder="placeholder"
                :readonly="loading || readonly"
                :disabled="loading || disabled"
                :autocomplete="$attrs.autocomplete || 'off'"
                :state="state"
                role="combobox"
                aria-autocomplete="list"
                :aria-expanded="isListVisible ? 'true' : 'false'"
                :aria-controls="isListVisible && filteredOptions.length > 0 ? listboxId : null"
                :aria-activedescendant="activeDescendant"
                @input="onUserInput"
                @focus="showList"
                @click="showList"
                @keydown.down.prevent="onArrowDown"
                @keydown.up.prevent="onArrowUp"
                @keydown.enter="onEnter"
            ></b-form-input>

            <b-input-group-append>
                <!-- mousedown.prevent: 버튼을 눌러도 입력칸 포커스를 유지해 focusout 으로 목록이 닫혔다 열리지 않게 -->
                <b-button v-if="clearable && hasSelection && !loading"
                          :disabled="disabled || readonly"
                          variant="outline-secondary"
                          class="clear-btn"
                          :aria-label="$t('선택 지우기')"
                          @mousedown.prevent
                          @click="clearInput"
                >
                    <fa :icon="['fas', 'times-circle']" />
                </b-button>
                <b-button :disabled="loading || disabled"
                          variant="outline-secondary"
                          class="dropdown-btn"
                          :aria-label="$t('목록 열기')"
                          :aria-expanded="isListVisible ? 'true' : 'false'"
                          @mousedown.prevent
                          @click="toggleList"
                >
                    <fa :icon="['fas', 'chevron-down']" />
                </b-button>
            </b-input-group-append>
        </b-input-group>

        <!-- 포털 렌더링: 드롭다운을 전역 타겟으로 출력.
             mousedown.prevent: 항목·묶음 머리·스크롤바를 눌러도 입력칸 포커스를 유지한다(모달 포커스 가두기와 충돌 방지).
             항목·묶음 머리는 tabindex=-1 — 포커스는 입력칸에 두고 방향키로 고른다 -->
        <portal v-if="isListVisible" to="global-dropdown">
            <div ref="portalRoot" class="autocomplete-portal" :style="portalStyle" @mousedown.prevent @focusout="onFocusOut">
                <b-list-group v-if="filteredOptions.length > 0"
                              :id="listboxId"
                              role="listbox"
                              class="autocomplete-results"
                              :style="{ maxHeight: listMaxHeight }"
                >
                    <template v-for="(option, index) in filteredOptions">
                        <b-list-group-item
                            v-if="option.isGroupHeader"
                            :key="`group-${option.label}`"
                            class="font-weight-bold bg-light"
                            button
                            tabindex="-1"
                            :aria-expanded="isGroupOpen(option.label) ? 'true' : 'false'"
                            @click="toggleGroup(option.label)"
                        >
                            <div class="d-flex justify-content-between align-items-center">
                                {{ option.label }}
                                <fa :icon="['fas', isGroupOpen(option.label) ? 'chevron-up' : 'chevron-down']" />
                            </div>
                        </b-list-group-item>

                        <b-list-group-item
                            v-else-if="isGroupOpen(option.group)"
                            :id="itemId(getNavigatableIndex(option))"
                            :key="`option-${getOptionValue(option)}-${index}`"
                            button
                            tabindex="-1"
                            role="option"
                            :aria-selected="isOptionSelected(option) ? 'true' : 'false'"
                            :active="focusedIndex === getNavigatableIndex(option)"
                            :variant="isOptionSelected(option) ? 'success' : null"
                            class="d-flex justify-content-between align-items-center"
                            @click="selectOption(option)"
                        >
                            <span class="pl-3">{{ getOptionText(option) }}</span>
                            <fa v-if="isOptionSelected(option)" :icon="['fas', 'check']" />
                        </b-list-group-item>
                    </template>
                </b-list-group>
                <!-- 결과가 없을 때 목록을 통째로 숨기면 고장인지 결과가 없는지 알 수 없다 -->
                <div v-else class="autocomplete-results autocomplete-empty text-muted" role="status">
                    {{ hasAnyOption ? $t('일치하는 항목이 없습니다.') : $t('선택할 항목이 없습니다.') }}
                </div>
            </div>
        </portal>
    </div>
</template>

<script>
export default {
    name: 'AutoCompleteSelect',
    // 넘겨받은 id·aria-*·autocomplete 는 입력칸(b-form-input)에 붙인다 — 호출처 label-for 가 입력칸을 가리키게
    inheritAttrs: false,
    model: {
        prop: 'selectedId',
        event: 'input'
    },
    props: {
        selectedId: { type: [Number, String, null], default: null },
        options: { type: Array, default: () => [] },
        loading: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        readonly: { type: Boolean, default: false },
        clearable: { type: Boolean, default: false },
        placeholder: { type: String, default: null },
        optionTextKey: { type: String, default: 'text' },
        optionValueKey: { type: String, default: 'value'},
        startExpanded: { type: Boolean, default: false },
        initiallyExpandedGroups: { type: Array, default: () => [] },
        listMaxHeight: { type: String, default: '520px' },
        state: { type: [Boolean, null], default: null },
    },
    data() {
        return {
            searchQuery: '',
            isListVisible: false,
            focusedIndex: -1,
            openGroups: [...this.initiallyExpandedGroups],
            // startExpanded 로 한 번 펼친 묶음 — 옵션이 다시 와도 사용자가 접은 묶음은 다시 열지 않는다
            seenGroups: [],
            // 포털 위치
            portalRect: { top: 0, left: 0, width: 0 },
        };
    },
    computed: {
        inputId() { return this.$attrs.id || `acs-${this._uid}`; },
        listboxId() { return `${this.inputId}-listbox`; },
        hasSelection() { return this.selectedId != null && this.selectedId !== ''; },
        // 로딩·비활성·읽기전용이면 목록을 열지도, 고르지도, 지우지도 않는다
        isBlocked() { return this.loading || this.disabled || this.readonly; },
        activeDescendant() {
            if (!this.isListVisible || this.focusedIndex < 0 || this.focusedIndex >= this.navigatableOptions.length) return null;
            return this.itemId(this.focusedIndex);
        },
        // 빈 묶음만 있는 배열도 '고를 항목 없음'으로 본다
        hasAnyOption() {
            return (this.options || []).some(opt => !(opt.group && opt.items) || opt.items.length > 0);
        },
        filteredOptions() {
            const query = this.searchQuery ? this.searchQuery.toLowerCase() : '';
            const results = [];
            if (!this.options) return [];
            this.options.forEach(option => {
                if (option.group && option.items) {
                    const filteredItems = query ? option.items.filter(item =>
                        this.getOptionText(item).toLowerCase().includes(query)
                    ) : [...option.items];

                    if (filteredItems.length > 0) {
                        results.push({ isGroupHeader: true, label: option.group });
                        filteredItems.forEach(item => {
                            results.push({ ...item, group: option.group });
                        });
                    }
                } else if (!query || this.getOptionText(option).toLowerCase().includes(query)) {
                    results.push(option);
                }
            });
            return results;
        },
        navigatableOptions() {
            return this.filteredOptions.filter(opt => !opt.isGroupHeader && this.isGroupOpen(opt.group));
        },
        flattenOptions() {
            return this.options.reduce((acc, opt) => {
                if (opt.group && opt.items) {
                    opt.items.forEach(item => { acc[item.id] = item; });
                } else { acc[opt.id] = opt; }
                return acc;
            }, {});
        },
        selectedItem(){
            if (this.selectedId != null) return this.flattenOptions[this.selectedId];
            return null;
        },
        portalStyle(){
            return {
                position: 'fixed',
                top: this.portalRect.top + 'px',
                left: this.portalRect.left + 'px',
                width: this.portalRect.width + 'px',
                zIndex: 3000,
            };
        }
    },
    watch: {
        selectedItem: {
            immediate: true,
            handler(newValue) {
                this.searchQuery = this.getOptionText(newValue);
            }
        },
        // 열린 채로 로딩·비활성으로 바뀌면 닫는다(미리보기·실행 중 선택이 바뀌지 않게)
        isBlocked(val) {
            if (val) this.hideList();
        },
        // 옵션이 늦게 와도(created 때 빈 배열) startExpanded 묶음이 펼쳐지게
        options() {
            this.focusedIndex = -1;
            if (this.startExpanded) this.expandNewGroups();
        },
        isListVisible(val){
            if (val) {
                this.$nextTick(() => {
                    this.updatePortalPosition();
                    window.addEventListener('resize', this.updatePortalPosition, { passive: true });
                    window.addEventListener('scroll', this.onAnyScroll, true);
                    document.addEventListener('click', this.handleClickOutside, true);
                });
            } else {
                window.removeEventListener('resize', this.updatePortalPosition, { passive: true });
                window.removeEventListener('scroll', this.onAnyScroll, true);
                document.removeEventListener('click', this.handleClickOutside, true);
            }
        }
    },
    created() {
        if (this.startExpanded) this.expandNewGroups();
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.updatePortalPosition, { passive: true });
        window.removeEventListener('scroll', this.onAnyScroll, true);
        document.removeEventListener('click', this.handleClickOutside, true);
    },
    methods: {
        groupNames() { return (this.options || []).filter(opt => opt.group).map(opt => opt.group); },
        // 호출처(ref)에서도 부른다
        expandAllGroups() {
            const allGroups = this.groupNames();
            this.seenGroups = allGroups;
            this.openGroups = [...new Set([...allGroups, ...this.initiallyExpandedGroups])];
        },
        expandNewGroups() {
            const fresh = this.groupNames().filter(group => !this.seenGroups.includes(group));
            if (fresh.length === 0) return;
            this.seenGroups = [...this.seenGroups, ...fresh];
            this.openGroups = [...new Set([...this.openGroups, ...fresh])];
        },
        // 검색 중에는 일치 항목이 접힌 묶음에 숨지 않게 펼쳐 보인다(저장된 접힘 상태는 그대로)
        isGroupOpen(groupName) {
            if (!groupName || this.searchQuery) return true;
            return this.openGroups.includes(groupName);
        },
        itemId(index) { return `${this.inputId}-item-${index}`; },
        toggleGroup(groupName) {
            if (this.searchQuery) return;
            this.focusedIndex = -1;
            const index = this.openGroups.indexOf(groupName);
            if (index > -1) this.openGroups.splice(index, 1); else this.openGroups.push(groupName);
        },
        onArrowDown() {
            if (!this.isListVisible) { this.showList(); return; }
            if (this.navigatableOptions.length === 0) return;
            this.focusedIndex = (this.focusedIndex + 1) % this.navigatableOptions.length;
            this.scrollToFocusedItem();
        },
        onArrowUp() {
            if (!this.isListVisible || this.navigatableOptions.length === 0) return;
            // 강조가 없을 때 위 화살표는 맨 아래 항목부터
            this.focusedIndex = this.focusedIndex <= 0 ? this.navigatableOptions.length - 1 : this.focusedIndex - 1;
            this.scrollToFocusedItem();
        },
        // 목록이 열려 있을 때만 Esc 를 여기서 쓴다 — 닫힌 상태의 Esc 는 모달 닫기 등으로 넘긴다
        onEsc(event) {
            if (!this.isListVisible) return;
            event.stopPropagation();
            this.hideList();
        },
        // 닫힌 상태의 Enter 는 막지 않는다(폼 제출). 열린 상태에서는 강조한(화면에 보이는) 항목만 고른다 — 강조 없이 맨 위 항목을 몰래 고르지 않게
        onEnter(event) {
            // 한글 조합을 끝내는 Enter 는 선택·제출로 쓰지 않는다
            if (!this.isListVisible || event.isComposing || event.keyCode === 229) return;
            event.preventDefault();
            const selected = this.focusedIndex > -1 ? this.navigatableOptions[this.focusedIndex] : null;
            if (selected) this.selectOption(selected);
        },
        scrollToFocusedItem() {
            this.$nextTick(() => {
                if (this.focusedIndex === -1) return;
                const activeItem = document.getElementById(this.itemId(this.focusedIndex));
                if (activeItem) activeItem.scrollIntoView({ block: 'nearest' });
            });
        },
        scrollToSelectedItem() {
            if (!this.selectedItem) return;
            const idx = this.getNavigatableIndex(this.selectedItem);
            if (idx === -1) return;
            const el = document.getElementById(this.itemId(idx));
            if (el) { el.scrollIntoView({ block: 'center' }); this.focusedIndex = idx; }
        },
        getNavigatableIndex(option) {
            return this.navigatableOptions.findIndex(item => this.getOptionValue(item) === this.getOptionValue(option));
        },
        getOptionValue(option) {
            if (option == null) return null;
            if (typeof option === 'object') return option[this.optionValueKey] || option[this.optionTextKey] || null;
            return option;
        },
        isOptionSelected(option) {
            if (!this.selectedItem || !option) return false;
            return this.getOptionValue(this.selectedItem) === this.getOptionValue(option);
        },
        getOptionText(option) {
            if (option == null) return '';
            if (typeof option === 'object') return option[this.optionTextKey] || '';
            return String(option);
        },
        // b-form-input 의 input 이벤트는 사용자가 친 글자에만 온다(v-model 갱신은 update) —
        // 프로그램이 바꾼 검색어와 구분하려고 두던 플래그·watcher 대신 쓴다
        onUserInput() {
            if (!this.isListVisible) this.showList();
            // 글자를 쳐서 결과가 있으면 첫 결과를 강조한다 — '검색어 + Enter' 가 화면에 보이는 그 항목을 고르게
            this.focusedIndex = this.searchQuery && this.navigatableOptions.length > 0 ? 0 : -1;
            this.scrollToFocusedItem();
        },
        selectOption(option) {
            if (this.isBlocked) return;
            this.isListVisible = false;
            this.searchQuery = this.getOptionText(option);
            this.$emit('input', option.id);
            this.$emit('click', option);
        },
        clearInput() {
            if (!this.hasSelection || this.isBlocked) return;
            this.searchQuery = '';
            this.$emit('input', null);
            this.$nextTick(() => { this.$refs.input.focus(); this.showList(); });
        },
        showList() {
            if (this.isBlocked) return;
            if (!this.isListVisible && this.selectedItem) this.searchQuery = '';
            this.isListVisible = true;
            this.$nextTick(() => { this.updatePortalPosition(); this.scrollToSelectedItem(); });
        },
        hideList() {
            if (!this.isListVisible) return;
            this.searchQuery = this.getOptionText(this.selectedItem);
            this.isListVisible = false;
            this.focusedIndex = -1;
        },
        toggleList() {
            if (this.isListVisible) { this.hideList(); return; }
            // 버튼은 mousedown.prevent 라 포커스가 없을 수 있다 — 키보드 조작과 focusout 닫기가 되도록 입력칸에 준다
            this.$refs.input.focus();
            this.showList();
        },
        // Tab 등으로 포커스가 입력칸을 떠나면(옆 지우기·펼치기 버튼 포함) 닫고 선택값 표시를 되돌린다.
        // 마우스 조작은 mousedown.prevent 라 포커스가 옮겨지지 않으므로 여기 오지 않는다
        onFocusOut(event) {
            const next = event.relatedTarget;
            const portalRoot = this.$refs.portalRoot;
            if (next && portalRoot && portalRoot.contains(next)) return;
            this.hideList();
        },
        updatePortalPosition() {
            const anchor = this.$el.querySelector('.autocomplete-input-group');
            if (!anchor) return;
            const rect = anchor.getBoundingClientRect();
            this.portalRect = { top: rect.bottom, left: rect.left, width: rect.width };
        },
        onAnyScroll() { if (this.isListVisible) this.updatePortalPosition(); },
        handleClickOutside(event) {
            const clickedInRoot = this.$el && this.$el.contains(event.target);
            const portalRoot = this.$refs.portalRoot;
            const clickedInPortal = portalRoot && portalRoot.contains(event.target);
            if (!clickedInRoot && !clickedInPortal) this.hideList();
        },
    }
};
</script>

<style lang="scss" scoped>
.autocomplete-wrapper {
    position: relative;
    width: 100%;
    display: block;
}
.list-group-item.active {
    background-color: #e9ecef;
    border-color: #dee2e6;
    color: #212529;
}
.autocomplete-portal { position: fixed; z-index: 3000; }
.autocomplete-empty { padding: .75rem 1.25rem; }
.autocomplete-results {
    position: static; // 포털 컨테이너가 위치를 담당
    overflow-y: auto;
    border: 1px solid #ced4da;
    border-top: none;
    border-bottom-left-radius: .25rem;
    border-bottom-right-radius: .25rem;
    background: #fff;
}
.autocomplete-input-group {
    .input-group-append .btn {
        background-color: white;
        border-color: #ced4da;
        &:hover { background-color: #e9ecef; }
        &:disabled{ background-color: #e9ecef; }
    }
    .input-group-prepend + .form-control,
    .form-control + .input-group-append,
    .input-group-append .btn + .btn { margin-left: -1px; }
    &.is-focused {
        $focus-color: #80bdff;
        .form-control,
        .input-group-prepend,
        .input-group-append .btn {
            border-color: $focus-color;
            box-shadow: 0 0 0 0.2rem rgba($focus-color, .25);
        }
        .form-control,
        .input-group-prepend { z-index: 1; }
        .input-group-append .btn { z-index: 2; }
    }
    // 실패/성공 상태에 따른 append 버튼 색상
    .form-control.is-invalid + .input-group-append .btn { border-color: #dc3545 !important; }
    .form-control.is-invalid + .input-group-append .btn:hover { border-color: #dc3545 !important; }
    .form-control.is-valid + .input-group-append .btn { border-color: #28a745 !important; }
    .form-control.is-valid + .input-group-append .btn:hover { border-color: #28a745 !important; }
}
</style>
